"use client";

import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import { Avatar, Empty, Input, Spin, Typography } from "antd";
import { SearchOutlined, UserDeleteOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import useDoctors from "../../../authDoctor/hooks/useDoctors";
import { getFollowedDoctorsThunk, unfollowDoctorThunk } from "../../../ProfileDoctor/redux/thunks/followsThunks";
import { ROUTES, DOCTOR_ROUTES } from "@/routes/patientRoutes/routePaths";
import "./FollowedDoctors.css";

const { Title, Text } = Typography;
const DEFAULT_AVATAR = "https://cdn-icons-png.flaticon.com/512/149/149071.png";

const normalizeId = (value) => String(value ?? "").trim().replace(/^DR_/i, "");
const pick = (...values) => values.find((value) => value != null && String(value).trim() && !["undefined", "null"].includes(String(value).trim().toLowerCase()));
const doctorId = (doctor) => normalizeId(doctor?.externalId ?? doctor?.doctorId ?? doctor?.medecinId ?? doctor?.id ?? doctor?._id ?? doctor?.userId);
const followeeId = (follow) => normalizeId(follow?.followeeId ?? follow?.followee_id ?? follow?.doctorId ?? follow?.doctor_id ?? follow?.doctor?.id ?? follow?.followee?.id);

const mapDoctor = (doctor) => {
  if (!doctor || typeof doctor !== "object") return null;
  const id = doctorId(doctor);
  if (!id) return null;
  const name = pick(doctor.fullName, doctor.name, doctor.doctorName, [doctor.firstName, doctor.lastName].filter(Boolean).join(" "), doctor.user?.fullName) || "Docteur";
  const specialty = pick(doctor.specialty, doctor.speciality, doctor.specialtyName, doctor.user?.specialty) || "General Practitioner";
  const image = pick(doctor.avatarUrl, doctor.avatar, doctor.photo, doctor.photoUrl, doctor.imageUrl, doctor.profilePicture, doctor.picture, doctor.image, doctor.user?.avatarUrl, doctor.user?.avatar) || DEFAULT_AVATAR;
  return { id, name, specialty, image, raw: doctor };
};

const ChatBubbleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path d="M3.333 9.723c0-3.5 2.985-6.39 6.667-6.39s6.667 2.89 6.667 6.39c0 3.502-2.985 6.391-6.667 6.391-.72 0-1.412-.11-2.06-.313L4.167 17.5l1.06-2.987a6.24 6.24 0 0 1-1.894-4.79z" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

function FollowedDoctors({ onMessage, limit = 6 }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { doctors = [], loading: doctorsLoading, error: doctorsError } = useDoctors();
  const currentUserId = useSelector((state) => state.auth?.user?.id ?? state.auth?.user?._id ?? state.patient?.user?.id ?? state.patient?.user?._id ?? null);
  const followedDoctors = useSelector((state) => state.follows?.followedDoctors || []);
  const followsLoading = useSelector((state) => Boolean(state.follows?.loading));
  const followsError = useSelector((state) => state.follows?.error || null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [removedIds, setRemovedIds] = useState(() => new Set());
  const [unfollowingId, setUnfollowingId] = useState(null);

  useEffect(() => {
    if (currentUserId) dispatch(getFollowedDoctorsThunk({ followerId: String(currentUserId) }));
  }, [dispatch, currentUserId]);

  const doctorsById = useMemo(() => {
    const result = new Map();
    doctors.forEach((doctor) => {
      const mapped = mapDoctor(doctor);
      if (mapped) result.set(mapped.id, mapped);
    });
    return result;
  }, [doctors]);

  const followOnlyDoctors = useMemo(() => {
    const followsById = new Map();
    followedDoctors.forEach((follow) => {
      const id = followeeId(follow);
      if (id) followsById.set(id, follow);
    });
    return Array.from(followsById, ([id, follow]) =>
      doctorsById.get(id) ?? mapDoctor(follow.doctor ?? follow.followee ?? follow)
    ).filter(Boolean);
  }, [doctorsById, followedDoctors]);

  const filteredDoctors = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return followOnlyDoctors.filter((doctor) =>
      !removedIds.has(doctor.id) &&
      (!term || doctor.name.toLowerCase().includes(term) || doctor.specialty.toLowerCase().includes(term))
    );
  }, [followOnlyDoctors, removedIds, searchTerm]);

  const handleUnfollow = useCallback(async (doctor) => {
    if (!currentUserId || !doctor.id || unfollowingId) return;
    setUnfollowingId(doctor.id);
    setRemovedIds((previous) => new Set(previous).add(doctor.id));
    try {
      await dispatch(unfollowDoctorThunk({ followerId: String(currentUserId), followeeId: doctor.id })).unwrap();
    } catch {
      setRemovedIds((previous) => {
        const next = new Set(previous);
        next.delete(doctor.id);
        return next;
      });
    } finally {
      setUnfollowingId(null);
    }
  }, [currentUserId, dispatch, unfollowingId]);

  const openProfile = useCallback((doctor) => navigate(DOCTOR_ROUTES.PROFILE_DOCTOR(doctor.id)), [navigate]);
  const messageDoctor = useCallback((doctor) => {
    const selectedContact = { id: doctor.id, userId: doctor.id, doctorId: doctor.id, fullName: doctor.name, name: doctor.name, specialty: doctor.specialty, avatar: doctor.image, avatarUrl: doctor.image, role: "doctor", isDoctor: true, type: "doctor" };
    navigate(`${ROUTES.MESSAGING}?doctorId=${doctor.id}`, { state: { selectedContact, autoOpen: true, fromFollowedDoctors: true, contactId: doctor.id } });
    onMessage?.(selectedContact);
  }, [navigate, onMessage]);

  const isLoading = doctorsLoading || followsLoading;
  const error = doctorsError || followsError;
  const displayedDoctors = isExpanded ? filteredDoctors : filteredDoctors.slice(0, limit);

  return (
    <div className="fd-card">
      <div className="fd-card__head"><Title level={4} className="fd-card__title">{t("followedDoctors.title")}</Title></div>
      <div className="fd-card__divider" />
      <div className="fd-search"><Input placeholder={t("followedDoctors.searchPlaceholder")} prefix={<SearchOutlined />} value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} allowClear /></div>
      <div className={`fd-content ${isExpanded || !Number.isFinite(limit) ? "fd-content--scrollable" : ""}`}>
        {isLoading ? <div className="fd-loading"><Spin tip={t("followedDoctors.loading")} /></div> : error ? <div className="fd-error"><Text type="danger">{error}</Text></div> : filteredDoctors.length === 0 ? <Empty description={searchTerm ? t("followedDoctors.emptySearch") : t("followedDoctors.emptyDefault")} image={Empty.PRESENTED_IMAGE_SIMPLE} /> : (
          <ul className="fd-list">
            {displayedDoctors.map((doctor) => (
              <li key={doctor.id} className="fd-item">
                <div className="fd-item__left">
                  <button type="button" className="fd-avatar-btn" onClick={() => openProfile(doctor)}>{doctor.image === DEFAULT_AVATAR ? <div className="fd-avatar-fallback">{doctor.name.slice(0, 2).toUpperCase()}</div> : <Avatar src={doctor.image} size={44} />}</button>
                  <div className="fd-item__text"><button type="button" className="fd-name-btn" onClick={() => openProfile(doctor)}><Text className="fd-name">{doctor.name}</Text></button><Text className="fd-spec">{doctor.specialty}</Text></div>
                </div>
                <div className="fd-item__actions">
                  <button className="fd-item__action fd-item__action--message" type="button" onClick={() => messageDoctor(doctor)}><ChatBubbleIcon /></button>
                  <button className="fd-item__action fd-item__action--danger" type="button" disabled={unfollowingId === doctor.id} onClick={() => handleUnfollow(doctor)} title={t("followedDoctors.unfollow", "Unfollow")}><UserDeleteOutlined /></button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

FollowedDoctors.propTypes = { onMessage: PropTypes.func, limit: PropTypes.number };
export default memo(FollowedDoctors);